import {weatherIconKey} from '../lib/weather-icons.js';
import {usableForecastSteps, forecastTemporalCoverage} from '../lib/forecast-coverage.js';
import {fetchForecastPayload} from '../lib/forecast-payload.js';
import {is3DView} from '../lib/camera-state.js';
import {createSpriteAtlasPruner} from '../lib/sprite-atlas.js';
import { isMapStyleReady } from '../mapcore.js';
// Layer PREVISIONE località v2 — port di assets/js/map-forecast.js (v1, fonte
// di verità): endpoint /api/forecast-map.php (param `mode`, default 'timeline';
// risposta {success, available_steps:[{key,date,time,label,items_count}],
// items_by_step:{key:[item…]}, available_days, items_by_day}), set località
// filtrati per zoom (regional/major/extended + fallback popolazione, soglie
// IDENTICHE), decluttering per collisione, marker "pill" temperatura.
//
// Differenza SOLO architetturale (motore MapLibre): il divIcon
// .ws-forecast-marker v1 è replicato come SPRITE CANVAS (stessa geometria del
// CSS v1: card 15px radius bianca traslucida, coda a rombo, icona 34px in box
// sfumato, temperatura 14px + nome 10px), symbol layer GPU con ancora bottom
// sulla punta della coda; il popup previsione è un maplibregl.Popup con l'HTML
// v1 (CSS portato in <style> iniettato, pattern marine-extra.js).
//
// Modalità: 'hourly' (la timeline unificata seleziona lo step ORARIO più
// vicino via showForecastAtTime). Le modalità period/daily del widget v1
// restano fuori (il dock v2 è la timeline unificata, non il widget forecast).
//
// API: initForecast(map), setForecastEnabled(bool), showForecastAtTime(T).

import { state as stationsState, inProvince } from './stations.js';
import { coveredTimelineEntry } from '../lib/forecast-time.js';

const ICON_BASE = '/images/meteo/';
const SRC = 'ws-forecast';
const LYR = 'ws-forecast-symbols';
const MAX_ATLAS_IMAGES = 320;

// ======== Mappature icone (port 1:1 map-forecast.js) ========
// ======== Set località per fascia zoom (port 1:1 map-forecast.js) ========
const FORECAST_REGIONAL_LOCALITIES = new Set([
    'agrigento',
    'caltanissetta',
    'catania',
    'enna',
    'messina',
    'palermo',
    'ragusa',
    'siracusa',
    'trapani'
]);
const FORECAST_MAJOR_LOCALITIES = new Set([
    ...FORECAST_REGIONAL_LOCALITIES,
    'acireale',
    'alcamo',
    'augusta',
    'avola',
    'bagheria',
    'barcellona pozzo di gotto',
    'caltagirone',
    'capo d orlando',
    'castelvetrano',
    'cefalu',
    'comiso',
    'gela',
    'licata',
    'lampedusa',
    'lipari',
    'marsala',
    'mazara del vallo',
    'milazzo',
    'modica',
    'noto',
    'pantelleria',
    'paterno',
    'partinico',
    'piazza armerina',
    'sciacca',
    'termini imerese',
    'taormina',
    'vittoria'
]);
const FORECAST_EXTENDED_LOCALITIES = new Set([
    ...FORECAST_MAJOR_LOCALITIES,
    'adrano',
    'agrigento',
    'barrafranca',
    'belpasso',
    'biancavilla',
    'bronte',
    'brolo',
    'canicatti',
    'capaci',
    'carini',
    'castelbuono',
    'castellammare del golfo',
    'catania fontanarossa',
    'chiaramonte gulfi',
    'cinisi',
    'corleone',
    'favara',
    'floridia',
    'giarre',
    'grammichele',
    'lentini',
    'leonforte',
    'mazzarino',
    'menfi',
    'misterbianco',
    'monreale',
    'nicosia',
    'niscemi',
    'palma di montechiaro',
    'patti',
    'pozzallo',
    'racalmuto',
    'randazzo',
    'ravanusa',
    'ribera',
    'riesi',
    'rosolini',
    'san cataldo',
    'sant agata di militello',
    'santa croce camerina',
    'scicli',
    'sortino',
    'terrasini',
    'troina',
    'valderice',
    'villabate'
]);
const FORECAST_POPULATION_FALLBACK = new Map(Object.entries({
    'palermo': 630167,
    'catania': 298762,
    'messina': 219948,
    'siracusa': 117055,
    'marsala': 80524,
    'gela': 73854,
    'ragusa': 73500,
    'vittoria': 63740,
    'caltanissetta': 60151,
    'agrigento': 55849,
    'trapani': 54749,
    'modica': 54581,
    'acireale': 51536,
    'mazara del vallo': 50300,
    'paterno': 47451,
    'bagheria': 53999,
    'enna': 26300,
    'monreale': 38300,
    'alcamo': 44400,
    'misterbianco': 49400,
    'augusta': 35200,
    'niscemi': 25700,
    'adrano': 35000,
    'partinico': 30600,
    'lentini': 23800,
    'licata': 35300,
    'caltagirone': 36800,
    'milazzo': 31200,
    'canicatti': 33400,
    'barcellona pozzo di gotto': 40500,
    'castelvetrano': 30000,
    'termini imerese': 25400,
    'sciacca': 39300
}));

const state = {
    map: null,
    active: false,
    loaded: false,
    payload: null,
    mode: 'hourly',
    key: '',
    loadPromise: null,
    _renderSeq: 0,
    _debounce: null,
    _images: new Set(),      // chiavi sprite nell'atlas MapLibre
    _icons: new Map(),       // iconKey -> Promise<HTMLImageElement|null>
    _popup: null,
};

// ======== helper (port 1:1) ========
function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, char => ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;'
    }[char]));
}
function numberOrNull(value) {
    if (value === null || value === undefined || value === '') return null;
    const number = Number.parseFloat(value);
    return Number.isFinite(number) ? number : null;
}
function firstValue(...values) {
    return values.find(value => value !== null && value !== undefined && value !== '');
}
function itemLat(item) {
    return numberOrNull(firstValue(item.lat, item.latitude));
}
function itemLon(item) {
    return numberOrNull(firstValue(item.lon, item.lng, item.longitude));
}
function itemId(item) {
    return String(firstValue(item.id, item.locality_id, item.name, `${itemLat(item)},${itemLon(item)}`));
}
function normalizeLocalityName(value) {
    return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[’']/g, ' ')
        .replace(/[^a-z0-9]+/gi, ' ')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();
}
function hourFromValue(value) {
    const match = String(value || '').match(/(?:^|[T\s])(\d{1,2})(?::|\.)/);
    if (!match) return null;
    const hour = Number(match[1]);
    return Number.isFinite(hour) && hour >= 0 && hour <= 23 ? hour : null;
}
function hourFromEntry(entry) {
    return [entry?.time, entry?.datetime, entry?.key, entry?.label, entry?.long_label]
        .map(hourFromValue)
        .find(value => value !== null) ?? null;
}
function hourFromItem(item) {
    // fascia (period): l'ora rappresentativa (9/15/21) guida l'icona giorno/notte
    if (Number.isFinite(Number(item?._periodHour))) return Number(item._periodHour);
    return [item?.time, item?.forecast_time, item?.datetime, item?.date_time, item?.valid_time, state.key]
        .map(hourFromValue)
        .find(value => value !== null) ?? null;
}
function isNight(item) {
    const hour = hourFromItem(item);
    return hour !== null && (hour < 7 || hour >= 19);
}
function iconKey(item) {
    return weatherIconKey(firstValue(item.icon_key, item.weather_icon, item.icon), item.weather_code, isNight(item));
}
function labelFromEntry(entry) {
    if (!entry) return '--';
    return String(firstValue(entry.long_label, entry.label, entry.datetime, entry.key) || '--');
}

// ======== step orari (port usableHourlySteps v1) ========
function usableHourlySteps() { return usableForecastSteps(state.payload); }
function currentEntry() {
    return usableHourlySteps().find(entry => entry.key === state.key) || null;
}
function itemsForKey(key) {
    return state.payload?.items_by_step?.[key] || [];
}
function entryTimeMs(entry) {
    const raw = firstValue(entry?.valid_time, entry?.datetime, entry?.date_time, entry?.time_full, entry?.key);
    const parsed = Date.parse(String(raw || ''));
    if (Number.isFinite(parsed)) return parsed;
    const date = dateKeyFromEntry(entry);
    if (!date) return 0;
    let hour = hourFromEntry(entry);
    if (hour === null) hour = entry?.period?.iconHour ?? 12; // giorno/fascia: ora rappresentativa
    return Date.parse(`${date}T${String(hour).padStart(2, '0')}:00:00`);
}
function nearestEntryForTime(targetMs) {
    // mode-aware: scorrendo la timeline nel futuro si aggancia allo step/fascia/giorno più vicino
    const entries = entriesForMode(state.mode).filter(entry => entryTimeMs(entry) > 0);
    if (!entries.length || !Number.isFinite(targetMs)) return null;
    return entries.reduce((best, entry) => {
        if (!best) return entry;
        return Math.abs(entryTimeMs(entry) - targetMs) < Math.abs(entryTimeMs(best) - targetMs) ? entry : best;
    }, null);
}
function coveredHourlyEntryForTime(targetMs) {
    return coveredTimelineEntry(
        usableHourlySteps().filter(entry => entryTimeMs(entry) > 0), targetMs, entryTimeMs
    );
}

// ======== modalità Ore / Fasce / Giorni (port period/daily v1 map-forecast.js) ==
const DAY_PERIODS = [
    { id: 'morning',   label: 'Mattina',    shortLabel: 'Matt.', start: 6,  end: 12, iconHour: 9 },
    { id: 'afternoon', label: 'Pomeriggio', shortLabel: 'Pom.',  start: 12, end: 18, iconHour: 15 },
    { id: 'evening',   label: 'Sera',       shortLabel: 'Sera',  start: 18, end: 24, iconHour: 21 },
];
function dateKeyFromEntry(entry) {
    const m = String(firstValue(entry?.date, entry?.key, entry?.datetime, entry?.valid_time, entry?.label) || '').match(/(\d{4}-\d{2}-\d{2})/);
    return m ? m[1] : '';
}
function formatDayShort(dateStr) {
    const d = new Date(String(dateStr) + 'T12:00:00');
    if (isNaN(d.getTime())) return String(dateStr || '');
    return d.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric' });
}
function hourLabel(entry) {
    const h = hourFromEntry(entry);
    return h === null ? labelFromEntry(entry) : String(h).padStart(2, '0') + ':00';
}
// FASCE: raggruppa gli step orari per data:fascia (port periodEntries v1)
function periodEntries() {
    const byKey = new Map();
    usableHourlySteps().forEach(step => {
        const date = dateKeyFromEntry(step);
        const hour = hourFromEntry(step);
        if (!date || hour === null) return;
        const period = DAY_PERIODS.find(p => hour >= p.start && hour < p.end);
        if (!period) return;
        const key = `${date}:${period.id}`;
        if (!byKey.has(key)) byKey.set(key, {
            key, date, period, shortLabel: period.shortLabel,
            dayLabel: formatDayShort(date), label: `${period.label} ${formatDayShort(date)}`, steps: [],
        });
        byKey.get(key).steps.push(step);
    });
    return Array.from(byKey.values());
}
// GIORNI: derivati dagli step orari (l'API items_by_day dà un solo temp/giorno,
// senza min/max → aggreghiamo noi dagli step per avere max/min reali)
function dayEntries() {
    const byDate = new Map();
    usableHourlySteps().forEach(step => {
        const date = dateKeyFromEntry(step);
        if (!date) return;
        if (!byDate.has(date)) byDate.set(date, {
            key: date, date, dayLabel: formatDayShort(date), shortLabel: formatDayShort(date), steps: [],
        });
        byDate.get(date).steps.push(step);
    });
    return Array.from(byDate.values());
}
function entriesForMode(mode) {
    if (mode === 'daily') return dayEntries();
    if (mode === 'period') return periodEntries();
    return usableHourlySteps();
}
// Aggregatore condiviso fasce/giorni: per località calcola max/min su tutte le
// temperature degli step, somma la pioggia, eredita il meteo "dominante" (severo).
// (port aggregatePeriod v1, generalizzato al giorno intero.)
function aggregateSteps(steps, iconHour) {
    const groups = new Map();
    (steps || []).forEach(step => {
        (state.payload?.items_by_step?.[step.key] || []).forEach(raw => {
            const id = itemId(raw);
            if (!groups.has(id)) groups.set(id, []);
            groups.get(id).push(raw);
        });
    });
    return Array.from(groups.values()).map(samples => {
        const base = samples[0];
        const temps = samples.flatMap(i => [i.temp, i.temperature, i.temp_max, i.temp_min]).map(numberOrNull).filter(v => v !== null);
        const rain = samples.map(i => numberOrNull(firstValue(i.rain_total, i.precipitation_mm))).filter(v => v !== null).reduce((s, v) => s + v, 0);
        const dominant = samples.find(i => /temporale|pioggia|neve|nubifragio/i.test(String(firstValue(i.weather_label, i.icon_key, i.weather_code)))) || base;
        return {
            ...base, ...dominant,
            temp: null,                                  // → tempParts mostra max/min
            temp_min: temps.length ? Math.min(...temps) : null,
            temp_max: temps.length ? Math.max(...temps) : null,
            rain_total: rain || null,
            _periodSummary: true,
            _periodHour: iconHour,                       // icona giorno corretta
        };
    });
}
function itemsForMode(mode, key) {
    if (mode === 'daily') { const e = dayEntries().find(d => d.key === key); return e ? aggregateSteps(e.steps, 13) : []; }
    if (mode === 'period') { const e = periodEntries().find(p => p.key === key); return e ? aggregateSteps(e.steps, e.period.iconHour) : []; }
    return itemsForKey(key);
}
function normalizedItemsForMode(mode, key) {
    return itemsForMode(mode, key)
        .map(item => ({ ...item, lat: itemLat(item), lon: itemLon(item) }))
        .filter(item => item.lat !== null && item.lon !== null);
}

// ======== visibilità per zoom (soglie IDENTICHE a map-forecast.js) ========
// Convenzione zoom v2: come stations.js, map.getZoom() MapLibre viene usato
// direttamente con le soglie v1 (stessa scelta del port dei marker stazione).
function itemPopulation(item) {
    const direct = numberOrNull(firstValue(item.population_2021, item.population, item.abitanti));
    if (direct !== null) return direct;
    const name = normalizeLocalityName(firstValue(item.municipality, item.comune, item.name, item.slug));
    return FORECAST_POPULATION_FALLBACK.get(name) || 0;
}
function isForecastLocalityVisible(item, zoom) {
    const name = normalizeLocalityName(firstValue(item.name, item.comune, item.slug));
    const zoomMin = numberOrNull(item.zoom_min);
    if (zoomMin !== null && zoom >= zoomMin - 0.2) return true;
    const population = itemPopulation(item);
    if (zoom >= 10.25) return true;
    if (population >= 45000 && zoom >= 8.75) return true;
    if (population >= 25000 && zoom >= 9.25) return true;
    if (population >= 12000 && zoom >= 9.9) return true;
    if (zoom >= 9.35) return FORECAST_EXTENDED_LOCALITIES.has(name);
    if (zoom >= 8.45) return FORECAST_MAJOR_LOCALITIES.has(name);
    return FORECAST_REGIONAL_LOCALITIES.has(name);
}
function scoreItem(item) {
    const name = normalizeLocalityName(firstValue(item.name, item.comune, item.slug));
    let score = Number(item.priority || 0);
    const population = itemPopulation(item);
    if (population > 0) score += Math.min(170, Math.log10(population) * 28);
    const zoomMin = numberOrNull(item.zoom_min);
    if (zoomMin !== null) score += Math.max(0, 14 - zoomMin) * 3;
    if (FORECAST_REGIONAL_LOCALITIES.has(name)) score += 220;
    else if (FORECAST_MAJOR_LOCALITIES.has(name)) score += 130;
    else if (FORECAST_EXTENDED_LOCALITIES.has(name)) score += 60;
    if (/temporale|nubifragio|forte|neve/i.test(String(firstValue(item.weather_label, item.icon_key)))) score += 20;
    if (numberOrNull(firstValue(item.rain_total, item.precipitation_mm)) > 0) score += 8;
    return score;
}

// ======== testi marker (port) ========
function formatTemp(value) {
    const number = numberOrNull(value);
    return number === null ? '--' : Math.round(number);
}
// [testo principale, testo secondario (min)] — replica tempHtml v1 (hourly):
// <strong>27°</strong> oppure <strong>max°</strong><em>min°</em>.
function tempParts(item) {
    const temp = numberOrNull(firstValue(item.temp, item.temperature));
    if (temp !== null) return [`${formatTemp(temp)}°`, ''];
    const max = numberOrNull(item.temp_max);
    const min = numberOrNull(item.temp_min);
    if (max !== null || min !== null) return [`${formatTemp(max)}°`, `${formatTemp(min)}°`];
    return ['', ''];
}
// tempText v1 VERBATIM (usato per larghezza marker e popup).
function tempText(item) {
    const temp = numberOrNull(firstValue(item.temp, item.temperature));
    if (temp !== null) return `${formatTemp(temp)} °C`;
    const max = numberOrNull(item.temp_max);
    const min = numberOrNull(item.temp_min);
    if (max !== null || min !== null) return `${formatTemp(max)}° / ${formatTemp(min)}°`;
    return '';
}
function windText(item) {
    const wind = numberOrNull(firstValue(item.wind_gust, item.wind_max, item.wind_avg));
    return wind === null ? '' : `${Math.round(wind)} km/h`;
}
function readableWeatherText(item) {
    const label = String(firstValue(item.weather_label, '') || '').trim();
    if (label && !/^\d+$/.test(label)) return label;
    return iconKey(item)
        .replace(/_(giorno|notte)$/i, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
}
function forecastMarkerName(item) {
    return String(firstValue(item.name, item.comune, item.municipality, '') || '').trim();
}
function estimateForecastTextWidth(text, fontSize) {
    return Array.from(String(text || '')).reduce((sum, char) => {
        if (char === ' ') return sum + fontSize * 0.32;
        if (/[A-ZÀ-Ü]/.test(char)) return sum + fontSize * 0.68;
        if (/[ilI\.,]/.test(char)) return sum + fontSize * 0.32;
        return sum + fontSize * 0.56;
    }, 0);
}
// Metriche v1 (variante non-dense: la modalità provincia v1 non è nella v2).
function forecastMarkerMetrics(item) {
    const name = forecastMarkerName(item);
    const nameWidth = estimateForecastTextWidth(name, 10);
    const tempWidth = Math.max(28, estimateForecastTextWidth(tempText(item) + ' °C', 14));
    const iconWidth = 34;
    const gap = 7;
    const padding = 16;
    const minWidth = 112;
    const maxWidth = 240;
    return {
        width: Math.ceil(Math.max(minWidth, Math.min(maxWidth, iconWidth + gap + Math.max(nameWidth, tempWidth) + padding))),
        height: 58
    };
}

// ======== decluttering per collisione (port pickNonOverlapping v1) ========
function forecastCollisionBox(item, zoom) {
    const point = state.map.project([item.lon, item.lat]);
    const metrics = forecastMarkerMetrics(item);
    const width = metrics.width;
    const height = metrics.height;
    const margin = zoom < 9.35 ? 14 : zoom < 10.25 ? 12 : 9;
    return {
        left: point.x - width / 2 - margin,
        right: point.x + width / 2 + margin,
        top: point.y - height / 2 - margin,
        bottom: point.y + height / 2 + margin,
        cx: point.x,
        cy: point.y
    };
}
function forecastBoxesOverlap(a, b) {
    const boxOverlap = a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
    if (boxOverlap) return true;
    const dx = a.cx - b.cx;
    const dy = a.cy - b.cy;
    return Math.hypot(dx, dy) < 54;
}
function pickNonOverlappingForecastItems(items, zoom, max) {
    const picked = [];
    const boxes = [];
    items.forEach(item => {
        if (picked.length >= max) return;
        const box = forecastCollisionBox(item, zoom);
        if (boxes.some(existing => forecastBoxesOverlap(existing, box))) return;
        boxes.push(box);
        picked.push(item);
    });
    return picked;
}
function paddedBounds(map, pad) {
    const b = map.getBounds();
    const dLng = (b.getEast() - b.getWest()) * pad;
    const dLat = (b.getNorth() - b.getSouth()) * pad;
    return {
        w: b.getWest() - dLng, e: b.getEast() + dLng,
        s: b.getSouth() - dLat, n: b.getNorth() + dLat,
        contains(lat, lon) { return lat >= this.s && lat <= this.n && lon >= this.w && lon <= this.e; },
    };
}
// Port visibleItems v1 (cap per zoom, path non-provincia).
function visibleItems() {
    const map = state.map;
    // With a pitched camera, geographic bounds can include places beyond the
    // horizon. Choose the localities actually visible in the current frame.
    const perspective = is3DView(map);
    const bounds = perspective ? null : paddedBounds(map, 0.1);
    const canvas = perspective ? map.getCanvas() : null;
    const zoom = map.getZoom();
    const max = zoom < 8.4 ? 10 : zoom < 9.2 ? 26 : zoom < 10.2 ? 58 : zoom < 11.2 ? 105 : zoom < 12.2 ? 170 : 260;
    // Filtro provincia (parità con le stazioni): con una provincia attiva le
    // previsioni FUORI provincia spariscono, non restano solo sbiadite dal ritaglio.
    const prov = stationsState.filters && stationsState.filters.province;
    const rows = normalizedItemsForMode(state.mode, state.key)
        .filter(item => {
            if (!perspective) return bounds.contains(item.lat, item.lon);
            const point = map.project([item.lon, item.lat]);
            return Number.isFinite(point.x) && Number.isFinite(point.y)
                && point.x >= -100 && point.x <= canvas.clientWidth + 100
                && point.y >= -60 && point.y <= canvas.clientHeight + 60;
        })
        .filter(item => !prov || inProvince(item, prov));
    const zoomFiltered = rows.filter(item => isForecastLocalityVisible(item, zoom));
    const candidates = zoomFiltered.length ? zoomFiltered : rows;
    return pickNonOverlappingForecastItems(
        candidates.sort((a, b) => scoreItem(b) - scoreItem(a)),
        zoom,
        max
    );
}

// ======== icone meteo (immagini webp, cache promise) ========
function loadIcon(key) {
    if (state._icons.has(key)) return state._icons.get(key);
    const promise = new Promise(resolve => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null); // v1: is-missing → fallback "☁"
        img.src = `${ICON_BASE}${key}.webp`;
    });
    state._icons.set(key, promise);
    return promise;
}

// ======== sprite canvas: replica del divIcon .ws-forecast-marker v1 ========
// Geometria dal CSS v1 (map.css:3034-3137): card bianca traslucida radius 15,
// bordo bianco, coda a rombo 12px, icona in box 34px sfumato ciano/verde,
// temperatura 14px peso max + eventuale min in muted, nome 10px muted.
const CARD_H = 50;
const TAIL = 7;
const SPRITE_PAD = 8; // spazio per l'ombra
const INK = '#10202d';
const MUTED = '#627383';

function spriteDpr() {
    return Math.max(1, Math.min(window.devicePixelRatio || 1, 2.5));
}
function roundedRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
}
function truncateToWidth(ctx, text, maxWidth) {
    if (ctx.measureText(text).width <= maxWidth) return text;
    let out = text;
    while (out.length > 1 && ctx.measureText(out + '…').width > maxWidth) out = out.slice(0, -1);
    return out + '…';
}

function forecastSpriteKey(item, metrics, hasIcon) {
    return ['fc', iconKey(item), tempText(item), forecastMarkerName(item), metrics.width, hasIcon ? 'i' : 'x'].join('|');
}

function buildForecastSprite(item, metrics, iconImg) {
    const dpr = spriteDpr();
    const w = metrics.width;
    const W = w + SPRITE_PAD * 2;
    const H = SPRITE_PAD + CARD_H + TAIL;
    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(W * dpr);
    canvas.height = Math.ceil(H * dpr);
    const ctx = canvas.getContext('2d', { alpha: true });
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.lineJoin = 'round';

    const left = SPRITE_PAD;
    const top = SPRITE_PAD;
    const cx = W / 2;

    // Card + ombra (box-shadow 0 12px 26px rgba(8,26,42,.18) approssimata)
    ctx.save();
    ctx.shadowColor = 'rgba(8,26,42,0.20)';
    ctx.shadowBlur = 9;
    ctx.shadowOffsetY = 4;
    roundedRect(ctx, left, top, w, CARD_H, 15);
    ctx.fillStyle = 'rgba(255,255,255,0.93)';
    ctx.fill();
    ctx.restore();

    // Coda a rombo (::after v1: 12×12 ruotato, bordo bianco)
    ctx.save();
    ctx.translate(cx, top + CARD_H);
    ctx.rotate(Math.PI / 4);
    ctx.fillStyle = 'rgba(255,255,255,0.93)';
    ctx.fillRect(-5, -5, 10, 10);
    ctx.strokeStyle = 'rgba(255,255,255,0.88)';
    ctx.lineWidth = 1;
    ctx.strokeRect(-5, -5, 10, 10);
    ctx.restore();

    // Bordo card sopra la coda
    roundedRect(ctx, left, top, w, CARD_H, 15);
    ctx.strokeStyle = 'rgba(255,255,255,0.88)';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Box icona 34×34 radius 12, gradiente ciano→verde
    const boxX = left + 8;
    const boxY = top + (CARD_H - 34) / 2;
    const grad = ctx.createLinearGradient(boxX, boxY, boxX + 34, boxY + 34);
    grad.addColorStop(0, 'rgba(110,231,255,0.26)');
    grad.addColorStop(1, 'rgba(25,169,116,0.14)');
    roundedRect(ctx, boxX, boxY, 34, 34, 12);
    ctx.fillStyle = grad;
    ctx.fill();
    ctx.strokeStyle = 'rgba(0,121,184,0.10)';
    ctx.lineWidth = 1;
    ctx.stroke();
    if (iconImg) {
        ctx.drawImage(iconImg, boxX + 2, boxY + 2, 30, 30);
    } else {
        // fallback .is-missing v1: "☁"
        ctx.font = '900 22px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#0b5cad';
        ctx.fillText('☁', boxX + 17, boxY + 18);
    }

    // Colonna testi: temperatura (riga 1) + nome (riga 2)
    const textX = boxX + 34 + 7;
    const textMax = left + w - 8 - (textX - left);
    const [tMain, tMin] = tempParts(item);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'alphabetic';
    ctx.font = '800 14px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial';
    ctx.fillStyle = INK;
    const tempY = top + CARD_H / 2 - 3;
    ctx.fillText(tMain, textX, tempY);
    if (tMin) {
        const mainW = ctx.measureText(tMain).width;
        ctx.fillStyle = MUTED;
        ctx.fillText(tMin, textX + mainW + 4, tempY);
    }
    ctx.font = '800 10px Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial';
    ctx.fillStyle = MUTED;
    ctx.fillText(truncateToWidth(ctx, forecastMarkerName(item), textMax), textX, top + CARD_H / 2 + 11);

    return { canvas, W, H, dpr };
}

// ======== popup (HTML v1, shell MapLibre) ========
function popupHtml(item) {
    const rain = numberOrNull(firstValue(item.rain_total, item.precipitation_mm));
    const condition = readableWeatherText(item);
    const temperature = tempText(item);
    const rows = [
        ['Pioggia', rain === null ? '' : `${rain.toFixed(rain >= 10 ? 0 : 1)} mm`],
        ['Vento', windText(item)],
        ['Umidita', item.humidity !== null && item.humidity !== undefined ? `${item.humidity}%` : ''],
        ['Pressione', item.pressure !== null && item.pressure !== undefined ? `${item.pressure} hPa` : '']
    ].filter(([, value]) => value !== null && value !== undefined && value !== '');

    return `
        <article class="ws-forecast-popup">
            <header>
                <span>Weather Sicily Forecast</span>
                <h2>${escapeHtml(item.name || item.comune || 'Localita')}</h2>
                <div class="ws-forecast-popup__meta">
                    <small>${escapeHtml(labelFromEntry(currentEntry()))}</small>
                    <small>${escapeHtml(firstValue(item.province, item.province_name, item.provincia, 'Sicilia'))}</small>
                </div>
            </header>
            <div class="ws-forecast-popup__summary">
                <span class="ws-forecast-popup__icon"><img src="${ICON_BASE}${escapeHtml(iconKey(item))}.webp" alt="" onerror="this.style.display='none'"></span>
                <div>
                    <strong>${escapeHtml(condition)}</strong>
                    ${temperature ? `<em>${escapeHtml(temperature)}</em>` : ''}
                </div>
            </div>
            <dl>${rows.map(([label, value]) => `<div><dt>${escapeHtml(label)}</dt><dd>${escapeHtml(value)}</dd></div>`).join('')}</dl>
        </article>
    `;
}

// CSS del popup: port di .ws-forecast-popup (map.css v1), shell adattata a MapLibre.
const FC_CSS = `
.wsv2-forecast-popup-shell .maplibregl-popup-content {
    padding: 0; overflow: hidden; border-radius: 18px;
    border: 1px solid rgba(255,255,255,0.9); background: rgba(255,255,255,0.97);
    color: #10202d; box-shadow: 0 20px 48px rgba(8,26,42,0.24);
}
.wsv2-forecast-popup-shell .maplibregl-popup-tip { border-top-color: rgba(255,255,255,0.97); border-bottom-color: rgba(255,255,255,0.97); }
.wsv2-forecast-popup-shell .maplibregl-popup-close-button {
    top: 10px; right: 10px; width: 28px; height: 28px; display: grid; place-items: center;
    border-radius: 10px; color: rgba(8,26,42,0.62); background: rgba(255,255,255,0.72); font-size: 20px; line-height: 1;
}
.ws-forecast-popup { width: 292px; max-width: 82vw; padding: 14px; font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, Arial; }
.ws-forecast-popup h2, .ws-forecast-popup dl, .ws-forecast-popup dd { margin: 0; }
.ws-forecast-popup header { padding: 0 34px 12px 0; }
.ws-forecast-popup header > span { display: block; color: #0079b8; font-size: 10px; font-weight: 900; letter-spacing: .09em; line-height: 1; text-transform: uppercase; }
.ws-forecast-popup h2 { margin-top: 5px; color: #07131d; font-size: 20px; font-weight: 900; line-height: 1.08; }
.ws-forecast-popup__meta { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.ws-forecast-popup__meta small {
    min-height: 24px; display: inline-flex; align-items: center; padding: 0 8px;
    border: 1px solid rgba(0,121,184,0.12); border-radius: 999px; color: #627383;
    background: rgba(0,121,184,0.06); font-size: 11px; font-weight: 850;
}
.ws-forecast-popup__summary {
    display: grid; grid-template-columns: 58px minmax(0,1fr); align-items: center; gap: 12px;
    margin: 0 0 12px; padding: 12px; border: 1px solid rgba(16,32,45,0.08); border-radius: 14px;
    background: linear-gradient(135deg, rgba(110,231,255,0.16), rgba(25,169,116,0.10));
}
.ws-forecast-popup__icon {
    width: 58px; height: 58px; display: grid; place-items: center; border-radius: 18px;
    background: rgba(255,255,255,0.82); border: 1px solid rgba(255,255,255,0.9); box-shadow: 0 10px 22px rgba(8,26,42,0.12);
}
.ws-forecast-popup__icon img { width: 48px; height: 48px; object-fit: contain; }
.ws-forecast-popup__summary div { min-width: 0; display: grid; gap: 4px; }
.ws-forecast-popup__summary strong { color: #07131d; font-size: 15px; font-weight: 900; line-height: 1.15; }
.ws-forecast-popup__summary em { color: #0b5cad; font-size: 22px; font-style: normal; font-weight: 900; line-height: 1; }
.ws-forecast-popup dl { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 7px; }
.ws-forecast-popup dl div {
    display: grid; gap: 3px; min-height: 50px; padding: 9px 10px;
    border: 1px solid rgba(16,32,45,0.08); border-radius: 12px; background: rgba(16,32,45,0.035);
}
.ws-forecast-popup dt { color: #627383; font-size: 10px; font-weight: 850; letter-spacing: .05em; text-transform: uppercase; }
.ws-forecast-popup dd { color: #07131d; font-size: 13px; font-weight: 800; }
`;
let cssInjected = false;
function injectCss() {
    if (cssInjected) return;
    cssInjected = true;
    const style = document.createElement('style');
    style.textContent = FC_CSS;
    document.head.appendChild(style);
}

// ======== payload (port loadPayload v1) ========
async function loadPayload() {
    if (state.loaded) return true;
    if (state.loadPromise) return state.loadPromise;
    state.loadPromise = (async () => {
        let payload = null;
        try {
            payload = await fetchForecastPayload({cache: 'force-cache'});
        } catch (e) { /* fallback API sotto */ }
        if (!payload && window.WSWeatherApi?.get) {
            payload = await window.WSWeatherApi.get('/forecasts/map', { mode: 'timeline' });
        }
        if (!payload) throw new Error('Weather Sicily API non disponibile');
        if (!payload?.success || !Array.isArray(payload.available_steps) || !payload.items_by_step) {
            throw new Error(payload?.error || 'Previsioni non disponibili');
        }
        state.payload = payload;
        state.loaded = true;
        if (!state.key) state.key = usableHourlySteps()[0]?.key || '';
        return true;
    })().catch(error => {
        state.loadPromise = null;
        throw error;
    });
    return state.loadPromise;
}

// ======== layer MapLibre ========
function ensureLayer() {
    const map = state.map;
    if (map.getSource(SRC)) return;
    map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
    map.addLayer({
        id: LYR,
        type: 'symbol',
        source: SRC,
        layout: {
            'icon-image': ['get', 'sk'],
            'icon-pitch-alignment':'viewport','icon-rotation-alignment':'viewport',
            'icon-anchor': 'bottom', // ancora = punta della coda (v1 iconAnchor [w/2, h-2])
            'icon-allow-overlap': true,
            'icon-ignore-placement': true,
            'symbol-z-order': 'viewport-y',
            'symbol-sort-key': ['get', 'y'],
        },
    });
}

function clearFeatures() {
    const src = state.map?.getSource(SRC);
    if (src) src.setData({ type: 'FeatureCollection', features: [] });
}

// ======== barra modalità Ore/Fasce/Giorni (port tab v1 map-forecast.js) ========
function ensureModeBar() {
    if (state._modeBar) return state._modeBar;
    const bar = document.createElement('div');
    bar.className = 'wsv2-fc-modebar';
    bar.hidden = true;
    bar.innerHTML = `
        <div class="wsv2-fc-tabs" role="tablist" aria-label="Modalità previsione">
            <button type="button" data-mode="hourly" class="is-active">Ore</button>
            <button type="button" data-mode="period">Fasce</button>
            <button type="button" data-mode="daily">Giorni</button>
        </div>
        <div class="wsv2-fc-steps"></div>`;
    document.body.appendChild(bar);
    bar.querySelectorAll('.wsv2-fc-tabs button').forEach(b =>
        b.addEventListener('click', () => selectForecastMode(b.dataset.mode)));
    state._modeBar = bar;
    return bar;
}
function renderModeBar() {
    const bar = ensureModeBar();
    bar.hidden = !state.active || !state.payload;
    document.documentElement.classList.toggle('wsv2-forecast-active', !bar.hidden);
    if (bar.hidden) return;
    bar.querySelectorAll('.wsv2-fc-tabs button').forEach(b =>
        b.classList.toggle('is-active', b.dataset.mode === state.mode));
    const strip = bar.querySelector('.wsv2-fc-steps');
    const entries = entriesForMode(state.mode);
    strip.innerHTML = entries.map(e => {
        const day = e.dayLabel || formatDayShort(dateKeyFromEntry(e));
        const lbl = state.mode === 'hourly' ? hourLabel(e) : (e.shortLabel || day);
        const sub = state.mode === 'daily' ? '' : `<small>${escapeHtml(day)}</small>`;
        return `<button type="button" data-key="${escapeHtml(e.key)}" class="${e.key === state.key ? 'is-active' : ''}">${sub}<b>${escapeHtml(lbl)}</b></button>`;
    }).join('');
    strip.querySelectorAll('button').forEach(b =>
        b.addEventListener('click', () => selectForecastStep(state.mode, b.dataset.key)));
    strip.querySelector('.is-active')?.scrollIntoView({ inline: 'center', block: 'nearest' });
}
function selectForecastMode(mode) {
    const entries = entriesForMode(mode);
    if (!entries.length) return;
    // mantieni l'istante corrente se una entry della nuova modalità lo copre, altrimenti la prima
    const curMs = entryTimeMs(entriesForMode(state.mode).find(e => e.key === state.key) || {});
    state.mode = mode;
    let next = entries[0];
    if (curMs > 0) next = entries.reduce((best, e) =>
        Math.abs(entryTimeMs(e) - curMs) < Math.abs(entryTimeMs(best) - curMs) ? e : best, entries[0]);
    state.key = next.key;
    if (state._popup) { state._popup.remove(); state._popup = null; }
    if (state.map) render();
    renderModeBar();
}
function selectForecastStep(mode, key) {
    state.mode = mode;
    state.key = key;
    if (state._popup) { state._popup.remove(); state._popup = null; }
    if (state.map) render();
    renderModeBar();
}

async function render() {
    const map = state.map;
    if (!map || !state.active || !state.key) return;
    if (!isMapStyleReady(map)) {
        if (!state._pendingIdle) {
            state._pendingIdle = true;
            map.once('idle', () => { state._pendingIdle = false; render(); });
        }
        return;
    }
    ensureLayer();
    const seq = ++state._renderSeq;
    const rows = visibleItems();

    // Precarica le icone necessarie (cache promise: dopo il primo giro è sync).
    const iconKeys = Array.from(new Set(rows.map(iconKey)));
    const icons = new Map();
    await Promise.all(iconKeys.map(async key => icons.set(key, await loadIcon(key))));
    if (seq !== state._renderSeq || !state.active) return;

    const features = [];
    const used = new Set();
    for (const item of rows) {
        const metrics = forecastMarkerMetrics(item);
        const ik = iconKey(item);
        const iconImg = icons.get(ik) || null;
        const sk = forecastSpriteKey(item, metrics, !!iconImg);
        used.add(sk);
        if (!map.hasImage(sk)) {
            const sprite = buildForecastSprite(item, metrics, iconImg);
            const ctx = sprite.canvas.getContext('2d');
            map.addImage(sk, ctx.getImageData(0, 0, sprite.canvas.width, sprite.canvas.height), { pixelRatio: sprite.dpr });
            state._images.add(sk);
        }
        features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [item.lon, item.lat] },
            properties: { sk, fid: itemId(item), y: item.lat * -1 },
        });
    }
    map.getSource(SRC).setData({ type: 'FeatureCollection', features });

    state._usedSpriteKeys = used;
    state._appliedSpriteSeq = seq;
    state._pruneAtlas?.();
    renderModeBar();
    // I riquadri-isola in modalità futuro leggono la previsione dell'isola da qui:
    // avvisali a ogni render (cambio step timeline compreso).
    document.dispatchEvent(new CustomEvent('wsv2:forecast'));
}

// Previsioni dello step corrente (con temperatura) dentro un bounding box — usato
// dai riquadri-isola per mostrare la previsione di Pantelleria/Lampedusa nel futuro.
export function forecastItemsInBounds(west, east, south, north) {
    if (!state.active || !state.loaded) return [];
    const out = [];
    for (const it of normalizedItemsForMode(state.mode, state.key)) {
        if (it.lon < west || it.lon > east || it.lat < south || it.lat > north) continue;
        const temp = numberOrNull(firstValue(it.temp, it.temperature));
        const t = temp !== null ? temp : numberOrNull(it.temp_max);
        if (t === null) continue;
        out.push({ lat: it.lat, lon: it.lon, temp: t, name: forecastMarkerName(it) });
    }
    return out;
}

function scheduleRender() {
    if (!state.active) return;
    if (state._debounce) clearTimeout(state._debounce);
    state._debounce = setTimeout(() => { state._debounce = null; render(); }, 60);
}

function wireInteractions() {
    const map = state.map;
    map.on('mousemove', LYR, () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', LYR, () => { map.getCanvas().style.cursor = ''; });
    map.on('click', LYR, (e) => {
        const f = e.features && e.features[0];
        if (!f || !state.active) return;
        const item = normalizedItemsForMode(state.mode, state.key).find(row => itemId(row) === f.properties.fid);
        if (!item) return;
        if (state._popup) state._popup.remove();
        state._popup = new maplibregl.Popup({
            className: 'wsv2-forecast-popup-shell',
            closeButton: true,
            maxWidth: '330px',
            offset: 56, // sopra la pill (ancora bottom)
        }).setLngLat([item.lon, item.lat]).setHTML(popupHtml(item)).addTo(map);
    });
}

// ======== API pubblica ========
export function initForecast(map) {
    state.map = map;
    state._pruneAtlas = createSpriteAtlasPruner(map, SRC, {
        images: state._images, limit: MAX_ATLAS_IMAGES,
        getUsed: () => state._usedSpriteKeys || new Set(),
        isSettled: () => state._appliedSpriteSeq === state._renderSeq,
    });
    injectCss();
    // Come stations.js: layer creato subito (init avviene a mapready) così gli
    // handler delegati su LYR trovano il layer; se lo style non fosse ancora
    // pronto la crea il primo render() (retry su 'idle').
    try { ensureLayer(); } catch (e) { /* style non pronto */ }
    wireInteractions();
    map.on('moveend', scheduleRender);
    map.on('zoomend', scheduleRender);
    map.on('resize', scheduleRender);
    // Ridisegna quando cambia la provincia attiva (le previsioni fuori provincia
    // devono sparire subito). Solo al cambio provincia, non a ogni update live.
    let lastProv = null;
    document.addEventListener('wsv2:stations', () => {
        if (!state.active) return;
        const p = (stationsState.filters && stationsState.filters.province) || null;
        if (p !== lastProv) { lastProv = p; scheduleRender(); }
    });
}

export async function setForecastEnabled(enabled) {
    const on = !!enabled;
    if (on === state.active) return state.active;
    if (on) {
        try {
            state.active = true;
            await loadPayload();
            if (!state.active) return false; // disattivato durante il load
            await render();
            return true;
        } catch (error) {
            console.warn('[wsv2] forecast layer non disponibile:', error);
            state.active = false;
            clearFeatures();
            return false;
        }
    }
    state.active = false;
    if (state._debounce) { clearTimeout(state._debounce); state._debounce = null; }
    state._renderSeq += 1;
    if (state._popup) { state._popup.remove(); state._popup = null; }
    clearFeatures();
    renderModeBar(); // nasconde la barra Ore/Fasce/Giorni
    return false;
}

// Initial live maps need only coverage, not every forecast locality/hour.
let metadataPromise;
async function loadForecastMetadata() {
    if (state.loaded) return state.payload;
    if (!metadataPromise) metadataPromise = fetch('/api/forecast-map.php?mode=timeline&metadata=1', {
        headers: {Accept:'application/json'}, signal:AbortSignal.timeout(15000),
    }).then(async response => {
        if (!response.ok) throw new Error('Copertura previsionale non disponibile');
        const payload = await response.json();
        if (!payload?.success || !Array.isArray(payload.available_steps)) throw new Error('Copertura previsionale non valida');
        return payload;
    }).catch(error => { metadataPromise = null; throw error; });
    return metadataPromise;
}
export function prefetchForecast() { return loadForecastMetadata().catch(() => {}); }
export async function getForecastTemporalCoverage() {
    return forecastTemporalCoverage(await loadForecastMetadata(), entryTimeMs);
}

export async function showForecastAtTime(target) {
    await loadPayload();
    const targetMs = target instanceof Date
        ? target.getTime()
        : (typeof target === 'number' ? target : Date.parse(String(target || '')));
    const coveredHourlyEntry = coveredHourlyEntryForTime(targetMs);
    const entry = coveredHourlyEntry
        ? (state.mode === 'hourly' ? coveredHourlyEntry : nearestEntryForTime(targetMs))
        : null;
    if (!entry) {
        state.key = '';
        state._renderSeq += 1;
        if (state._popup) { state._popup.remove(); state._popup = null; }
        clearFeatures();
        renderModeBar();
        document.dispatchEvent(new CustomEvent('wsv2:forecast'));
        return false;
    }
    if (state.key !== entry.key) {
        state.key = entry.key;
        if (state._popup) { state._popup.remove(); state._popup = null; }
    }
    if (state.active) await render();
    return true;
}
